"use client";
import { Button, makeStyles, tokens } from "@fluentui/react-components";
import { useNavigate } from "react-router-dom";

import { ClientRoutes } from "../models/ClientRoutes";
import {
  useCreateActivityDraft,
  useGetActivityFields,
} from "../hooks/useActivities";
import ActivityForm from "../components/activity/ActivityForm";
import { ActivityFormData, CreateActivityRequest } from "../models/Activity";
import { validateFormFieldsForRequest } from "../services/FieldService";
import { useTranslation } from "react-i18next";
import { ArrowLeftFilled } from "@fluentui/react-icons";
import { replaceImageSrcWithImageIds } from "../services/ImageService";
import {
  getProcessFlowHeadSettingsForRequest,
  getProcessFlowSettingsForRequest,
} from "../services/ActivityService";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
});

function CreateActivity() {
  const classes = useClasses();
  const navigate = useNavigate();
  const { mutateAsync: createActivityDraft, isPending } =
    useCreateActivityDraft();
  const { data: viewDefinition, isLoading: viewDefinitionLoading } =
    useGetActivityFields();
  const { t } = useTranslation();

  async function onFormSubmit(
    formData: ActivityFormData,
    options?: { publishAfterSave?: boolean }
  ) {
    if (!viewDefinition) {
      return;
    }

    const requestFields = validateFormFieldsForRequest(
      formData.fieldsWithValues,
      viewDefinition.fields
    );

    // Convert base64 image src to imageIds using position from imageIdsWithProperties
    const serializedHtml = formData.richText
      ? replaceImageSrcWithImageIds(formData.richText)
      : formData.richText;

    const createRequest: CreateActivityRequest = {
      title: formData.title,
      serializedHtml: serializedHtml,
      fieldsWithValues: requestFields,
      toolIds: formData.toolIds,
      imageIds: formData.imageIds,
      imageVersionIds: formData.imageVersionIds,
      toolLinkIds: formData.toolLinkIds,
      processFlowSettings: getProcessFlowSettingsForRequest(formData),
      processFlowHeadSettings: getProcessFlowHeadSettingsForRequest(formData),
    };

    const createdActivity = await createActivityDraft(createRequest);

    navigate(`${ClientRoutes.Activities}/${createdActivity.versionId}`, {
      state: { openPublishOnLoad: options?.publishAfterSave === true },
    });
  }

  return (
    <div className={classes.root}>
      <h1>{t("createDraft")}</h1>
      <ActivityForm
        isLoading={isPending || viewDefinitionLoading}
        isRefetching={isPending || viewDefinitionLoading}
        onSubmit={onFormSubmit}
        activityFields={viewDefinition?.fields}
      />
      <div className={classes.buttons}>
        <Button
          icon={<ArrowLeftFilled />}
          size="small"
          onClick={() => navigate(-1)}
        >
          {t("back")}
        </Button>
      </div>
    </div>
  );
}

export default CreateActivity;
